// frontend/src/internal/InternalStaffManagement.js
// 自社スタッフ (internal_staff) の登録・編集・削除・権限変更ページ
// admin ロールのみアクセス可能。
//
// 新規追加フロー:
//   1. Supabase Dashboard (Authentication > Users) で先にユーザー作成
//   2. このページで「メアド + 名前 + ロール」を入力 → 内部で auth.users.id を取得
//   3. internal_staff に INSERT

import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useInternalStaff } from '../contexts/InternalStaffContext';

const ROLE_OPTIONS = [
  { value: 'admin',   label: 'admin (全権限)' },
  { value: 'support', label: 'support (閲覧 + 編集モード操作)' },
  { value: 'viewer',  label: 'viewer (閲覧のみ)' },
];

const InternalStaffManagement = () => {
  const navigate = useNavigate();
  const { internalStaff, loading, logout, logAudit } = useInternalStaff();
  const [staffList, setStaffList] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 新規追加フォーム state
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('viewer');
  const [adding, setAdding] = useState(false);

  // 編集中の行 state (id → patched fields)
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', is_active: true });
  const [updating, setUpdating] = useState(false);

  const fetchStaff = useCallback(async () => {
    setFetching(true);
    try {
      const { data, error: e } = await supabase
        .from('internal_staff')
        .select('id, user_id, email, name, role, is_active, created_at')
        .order('created_at', { ascending: false });
      if (e) throw e;
      setStaffList(data || []);
    } catch (e) {
      console.error('staff list:', e);
      setError(`一覧取得エラー: ${e.message}`);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (internalStaff?.role === 'admin') fetchStaff();
  }, [internalStaff, fetchStaff]);

  if (loading) return <div style={{ padding: 40 }}>読み込み中...</div>;
  if (!internalStaff) return <Navigate to="/internal/login" replace />;
  if (internalStaff.role !== 'admin') {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
        <h1>アクセス権限がありません</h1>
        <p>このページは admin ロールのみアクセス可能です。あなたのロール: <strong>{internalStaff.role}</strong></p>
      </div>
    );
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newEmail.trim() || !newName.trim() || !newPassword) {
      setError('メアド・名前・パスワードは必須です');
      return;
    }
    if (newPassword.length < 8) {
      setError('パスワードは 8 文字以上にしてください');
      return;
    }
    setAdding(true);
    try {
      // 🔥 Edge Function 経由で auth.users 作成 + internal_staff INSERT を一括実行
      //   service_role_key が必要なのでクライアント直からは不可。
      const { data, error: fnErr } = await supabase.functions.invoke('internal-staff-create', {
        body: {
          email: newEmail.trim(),
          password: newPassword,
          name: newName.trim(),
          role: newRole,
        },
      });
      if (fnErr) throw fnErr;
      if (!data?.success) throw new Error(data?.message || '追加に失敗しました');

      const reusedMsg = data.reused_existing_auth ? ' (既存 Auth ユーザーに紐付け)' : '';
      setSuccess(`✅ ${newEmail.trim()} を登録しました${reusedMsg}`);
      setNewEmail(''); setNewName(''); setNewPassword(''); setNewRole('viewer');
      await fetchStaff();
    } catch (e) {
      console.error('add staff:', e);
      setError(`追加エラー: ${e.message}`);
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, role: s.role, is_active: s.is_active });
    setError(''); setSuccess('');
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({ name: '', role: '', is_active: true }); };

  const handleUpdate = async (id) => {
    setError(''); setSuccess('');
    setUpdating(true);
    try {
      const { error: e } = await supabase
        .from('internal_staff')
        .update({
          name: editForm.name.trim(),
          role: editForm.role,
          is_active: !!editForm.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (e) throw e;
      setSuccess('✅ 更新しました');
      cancelEdit();
      await fetchStaff();
    } catch (e) {
      console.error('update staff:', e);
      setError(`更新エラー: ${e.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (s) => {
    if (s.user_id === internalStaff.user_id) {
      alert('自分自身は削除できません');
      return;
    }
    if (!window.confirm(`「${s.name}」(${s.email}) を internal_staff から削除しますか?\n`
      + '(Supabase Auth のユーザー自体は削除されません。 完全に削除するには Supabase Dashboard で別途行ってください。\n'
      + '監査ログは履歴として残ります)')) return;
    setError(''); setSuccess('');
    try {
      // 削除前に「誰を削除したか」の監査ログを残す (削除後だと FK SET NULL でリンク切れ)
      await logAudit('internal_staff_delete', {
        target: 'internal_staff',
        detail: {
          deleted_id: s.id, deleted_email: s.email,
          deleted_name: s.name, deleted_role: s.role,
        },
      });
      const { error: e } = await supabase
        .from('internal_staff')
        .delete()
        .eq('id', s.id);
      if (e) throw e;
      setSuccess(`🗑️ ${s.email} を削除しました (監査ログは残ります)`);
      await fetchStaff();
    } catch (e) {
      console.error('delete staff:', e);
      setError(`削除エラー: ${e.message}`);
    }
  };

  const cellStyle = { padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '13px' };
  const inputStyle = { padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc, #eff6ff)',
      padding: '24px',
    }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        {/* ヘッダー (戻るボタン群) */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '16px',
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#0f172a' }}>🔧 開発者管理</h1>
            <p style={{ color: '#64748b', fontSize: '13px', marginTop: 4, marginBottom: 0 }}>
              自社スタッフ (internal_staff) の登録・編集・削除・権限変更。 admin 専用。
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/internal/select-org')} style={{
              padding: '8px 14px', background: '#e0e7ff', color: '#3730a3',
              border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
            }}>
              ← 組織選択へ
            </button>
            <button onClick={logout} style={{
              padding: '8px 14px', background: '#e5e7eb',
              border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
            }}>
              ログアウト
            </button>
          </div>
        </div>

        {/* 通知 */}
        {error && (
          <div style={{
            background: '#fee2e2', color: '#7f1d1d',
            padding: '10px 14px', borderRadius: '8px',
            fontSize: '12px', marginTop: '16px', whiteSpace: 'pre-line',
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            background: '#dcfce7', color: '#166534',
            padding: '10px 14px', borderRadius: '8px',
            fontSize: '12px', marginTop: '16px',
          }}>{success}</div>
        )}

        {/* 新規追加フォーム */}
        <div style={{
          background: 'white', borderRadius: '14px', padding: '20px',
          marginTop: '20px', border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
            ➕ 新規追加
          </div>
          <div style={{
            background: '#eff6ff', color: '#1e40af',
            padding: '8px 12px', borderRadius: '6px',
            fontSize: '12px', marginBottom: '14px',
          }}>
            ℹ️ メアド + 名前 + 初期パスワード + ロール を入力するだけで、Auth ユーザー作成と internal_staff 登録が一括で完了します。
            既に同メアドの Auth ユーザーがあれば自動で紐付けます。
          </div>
          <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr auto', gap: '10px', alignItems: 'end' }}>
            <label>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 4 }}>メールアドレス *</div>
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                placeholder="dev@scrubedge.com" required style={inputStyle} />
            </label>
            <label>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 4 }}>表示名 *</div>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="例: 山田 太郎" required style={inputStyle} />
            </label>
            <label>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 4 }}>初期パスワード * (8文字以上)</div>
              <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8文字以上" required minLength={8} autoComplete="new-password" style={inputStyle} />
            </label>
            <label>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 4 }}>ロール</div>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)} style={inputStyle}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <button type="submit" disabled={adding} style={{
              padding: '8px 16px',
              background: adding ? '#94a3b8' : 'linear-gradient(135deg, #059669, #047857)',
              color: 'white', border: 'none', borderRadius: '8px',
              fontWeight: 700, fontSize: '13px',
              cursor: adding ? 'not-allowed' : 'pointer',
              height: '34px',
            }}>
              {adding ? '追加中...' : '追加'}
            </button>
          </form>
        </div>

        {/* 一覧 */}
        <div style={{
          background: 'white', borderRadius: '14px',
          marginTop: '20px', border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
            👥 登録済みスタッフ ({staffList.length}名)
          </div>
          {fetching ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>取得中...</div>
          ) : staffList.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>登録されたスタッフがいません</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: '#475569' }}>メアド / 名前</th>
                  <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: '#475569', width: '170px' }}>ロール</th>
                  <th style={{ ...cellStyle, textAlign: 'center', fontWeight: 700, color: '#475569', width: '80px' }}>有効</th>
                  <th style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, color: '#475569', width: '180px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((s) => {
                  const isEditing = editingId === s.id;
                  const isMe = s.user_id === internalStaff.user_id;
                  return (
                    <tr key={s.id} style={{ background: isEditing ? '#fefce8' : 'white' }}>
                      <td style={cellStyle}>
                        <div style={{ color: '#0f172a', fontWeight: 600 }}>
                          {isEditing ? (
                            <input type="text" value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              style={inputStyle} />
                          ) : (
                            <>{s.name} {isMe && <span style={{ fontSize: '10px', color: '#3b82f6', marginLeft: 6 }}>(自分)</span>}</>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{s.email}</div>
                      </td>
                      <td style={cellStyle}>
                        {isEditing ? (
                          <select value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            style={inputStyle}>
                            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                          </select>
                        ) : (
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px',
                            fontSize: '11px', fontWeight: 700,
                            background: s.role === 'admin' ? '#fee2e2' : (s.role === 'support' ? '#dbeafe' : '#f1f5f9'),
                            color:      s.role === 'admin' ? '#991b1b' : (s.role === 'support' ? '#1e40af' : '#475569'),
                          }}>{s.role}</span>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        {isEditing ? (
                          <input type="checkbox" checked={!!editForm.is_active}
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                        ) : (
                          s.is_active
                            ? <span style={{ color: '#16a34a' }}>✓</span>
                            : <span style={{ color: '#dc2626' }}>✕</span>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        {isEditing ? (
                          <>
                            <button onClick={() => handleUpdate(s.id)} disabled={updating} style={{
                              padding: '4px 10px', background: '#2563eb', color: 'white',
                              border: 'none', borderRadius: '6px', fontSize: '11px',
                              cursor: updating ? 'not-allowed' : 'pointer', marginRight: 4,
                            }}>保存</button>
                            <button onClick={cancelEdit} disabled={updating} style={{
                              padding: '4px 10px', background: '#e5e7eb',
                              border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
                            }}>取消</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(s)} style={{
                              padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8',
                              border: 'none', borderRadius: '6px', fontSize: '11px',
                              cursor: 'pointer', marginRight: 4,
                            }}>編集</button>
                            <button onClick={() => handleDelete(s)} disabled={isMe} style={{
                              padding: '4px 10px',
                              background: isMe ? '#f3f4f6' : '#fee2e2',
                              color: isMe ? '#9ca3af' : '#dc2626',
                              border: 'none', borderRadius: '6px', fontSize: '11px',
                              cursor: isMe ? 'not-allowed' : 'pointer',
                            }}>削除</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 16, fontSize: '12px', color: '#94a3b8' }}>
          💡 ロール早見表:<br />
          ・<strong>admin</strong>: 全権限。 他スタッフの追加・編集・削除・編集モード ON 可能<br />
          ・<strong>support</strong>: 全組織閲覧 + 編集モード ON/OFF 可能。 他スタッフ管理不可<br />
          ・<strong>viewer</strong>: 閲覧のみ。 編集モード ON 不可
        </div>
      </div>
    </div>
  );
};

export default InternalStaffManagement;
